// @flow
/* -------------------- Globals ----------------*/
import produce from 'immer';
import isEmpty from 'lodash/isEmpty';
import shuffle from 'lodash/shuffle';
import calcScore from '../functions/scoring/match';

/* -------------------- Action Types ----------------*/
import {
  START_SESSION,
  START_NEXT_BATCH,
  PLAY,
  MATCH,
  STOP_SESSION,
  COMPLETE_SESSION,
  STOP,
  WIN,
} from './actionTypes';

import {
  RESET_APP,
} from '../actionTypes';

/* -------------------- Helpers ----------------*/
import {
  setTarget,
} from '../functions/progressReducer';
import {
  getTimeTakenInSeconds,
} from '../dateutils';
import {
  countQuestions,
} from '../functions/distributionReducer';

/* -------------------- Types ----------------*/
import type {
  MatchGame,
  MatchGameState,
  MatchState,
  MatchTerm,
  MatchFact,
  Distribution,
} from './types';
import type { LearningOrders } from '../types';

import {
  initialState,
  empty,
} from './constants';
import {
  THRESHOLD_MASTERY_NORMAL_SESSION,
} from '../session/constants';
/* -------------------- Handlers ----------------*/
const leftOrRight = (left, right) => (left.item.factId ? left : right);

const summarize = (
  state: MatchState,
  data: *,
) => produce(state, (draft) => {
  const {
    games,
    gameStates,
    uniqueAnswers,
  } = draft;
  const allFacts = new Set();
  const uniqueFacts = new Set();
  let correct = 0;
  let all = 0;
  let totalSeconds = 0;

  games.forEach((g) => {
    g.facts.forEach((f) => {
      allFacts.add(f.factId);
    });
  });
  const scores = [];
  gameStates.forEach((gs) => {
    gs.actions.forEach((a) => {
      all += 1;
      correct += (a.correct ? 1 : 0);
      const fact = leftOrRight(a.left, a.right);
      // $FlowFixMe - leftOrRight ensures that this is a fact.
      uniqueFacts.add(fact.item.factId);
      scores.push(a.score);
    });
    totalSeconds += getTimeTakenInSeconds(gs.startTime, gs.endTime);
  });

  const accuracy = all === 0 ? 0 : (correct * 100.0 / all);
  const averageTime = totalSeconds / gameStates.length;

  draft.endTime = new Date().toISOString();
  draft.progressData = {
    target: Array.from(allFacts),
    answered: Array.from(uniqueFacts),
    skipped: [],
  };
  const uniqueAnswersWithContentId = [...new Set(Object.values(uniqueAnswers).map<any>(
    // $FlowFixMe
    ({ contentId }) => contentId,
  ))];
  const filteredReviewContents = data.filter(
    ({ content: { id } }) => uniqueAnswersWithContentId.includes(id),
  );
  draft.reviewedContents = filteredReviewContents;
  draft.summary = {
    accuracy,
    timePerAnswer: averageTime,
  };
});

const handleReset = state => produce(state, (draft) => {
  draft.currentGameId = null;
  draft.currentGameState = null;
  draft.events = [];
  draft.games = [];
  draft.gameStates = [];
  draft.sessionCreated = false;
  draft.sessionId = null;
  draft.reviewedContents = [];
  draft.requestedQuestionsCount = 0;
  draft.hasMor = false;
  draft.answeredFacts = { };
  draft.uniqueAnswers = { };
  draft.reviewGameState = null;
});
/* ---------------  Begin: Code to be shared through algorithms --------- */
const factText = (f: MatchFact) => {
  const prompt = f.prompt || '';
  const name = f.name === empty.name || isEmpty(f.name) ? '' : f.name;
  return `${prompt.trim()}:${name.trim()}`;
};

const matchingFact = (term: MatchTerm, fact: MatchFact) => {
  const factWithId = term.facts.find(tf => tf.factId === fact.factId);
  if (factWithId) {
    return factWithId;
  }
  if (fact.name === empty.name) {
    return false;
  }
  const text = factText(fact);
  const found = term.facts.find(tf => factText(tf) === text);
  return found;
};
/* ---------------  End: Code to be shared through algorithms --------- */

const toGameState = (game: MatchGame): MatchGameState => {
  const gameId = game.id;
  const mkId = (o: MatchTerm | MatchFact, idx) => {
    let id: string = '';
    if (typeof o.termId === 'string') {
      id = o.termId;
    } else if (typeof o.factId === 'string') {
      id = o.factId;
    }
    return [id, `${id}-${idx}`];
  };

  const matches = {};
  game.facts.forEach((f) => {
    game.terms.forEach((t) => {
      const foundFact = matchingFact(t, f);
      // If term has the given fact,
      if (foundFact) {
        // Add the term, fact pair to the term matches.
        const termFacts = matches[t.termId] || [];
        // TODO: Get dist from term t.distribution
        termFacts.push({
          matchId: f.factId,
          termId: t.termId,
          factId: foundFact.factId,
          previousScore: foundFact.previousScore,
          currentScore: foundFact.currentScore,
          contentId: t.contentId,
        });
        matches[t.termId] = termFacts;
        // Add the term, fact pair to fact matches.
        const factTerms = matches[f.factId] || [];
        factTerms.push({
          matchId: t.termId,
          termId: t.termId,
          factId: foundFact.factId,
          previousScore: foundFact.previousScore,
          currentScore: foundFact.currentScore,
          contentId: t.contentId,
        });
        matches[f.factId] = factTerms;
      }
    });
  });

  const getMatches = (item: MatchTerm | MatchFact) => {
    let result;
    if (typeof item.termId === 'string') {
      result = matches[item.termId];
    } else if (typeof item.factId === 'string') {
      result = matches[item.factId];
    }
    return result || [];
  };

  const cards = [
    ...game.facts,
    ...game.terms,
  ].map((i: MatchTerm | MatchFact, index) => {
    const [matchId, id] = mkId(i, index);
    return {
      matchId,
      id,
      item: i,
      matches: getMatches(i),
    };
  });

  const startTime = new Date().toISOString();
  const gameState = {
    gameId,
    actions: [],
    game: shuffle(cards),
    startTime,
    timeTaken: 0,
  };
  return gameState;
};

const handleStartSession = (
  games: MatchGame[],
  state: MatchState,
  sessionId: string,
  learningOrder: LearningOrders,
  distribution: Distribution<number>,
  hasMore: boolean,
) => produce(state, (draft) => {
  let ids = [];
  games.forEach(({
    facts,
  }) => {
    ids = facts.map(({
      factId,
    }) => factId);
  });
  draft.gameStates = [];
  draft.games = games;
  const startTime = new Date().toISOString();
  draft.startTime = startTime;
  draft.sessionId = sessionId;
  draft.sessionCreated = true;
  draft.progressData = setTarget(ids);
  draft.learningOrder = learningOrder;
  draft.hasMore = hasMore;
  draft.requestedQuestionsCount = countQuestions(distribution);
});

const handleNextBatch = (
  newGames: MatchGame[],
  hasMore: boolean,
  state: MatchState,
) => produce(state, (draft) => {
  const { games: currentGames, progressData } = state;
  let ids = [];
  newGames.forEach(({ facts }) => {
    ids = facts.map(({ factId }) => factId);
  });
  draft.games = [...currentGames, ...newGames];
  const currentTarget = progressData ? progressData.target : [];
  draft.progressData = setTarget([...currentTarget, ...ids]);
  draft.hasMore = hasMore;
});

const getProgressData = (
  gameStates: MatchGameState[],
  games: MatchGame[],
) => {
  const allFacts = new Set();
  const uniqueFacts = new Set();
  games.forEach((g) => {
    g.facts.forEach((f) => {
      allFacts.add(f.factId);
    });
  });

  gameStates.forEach((currentGameState) => {
    currentGameState.actions.forEach((a) => {
      const fact = leftOrRight(a.left, a.right);
      // $FlowFixMe - leftOrRight ensures that this is a fact.
      if (fact.item.factId) {
        uniqueFacts.add(fact.item.factId);
      }
    });
  });

  const newProgressData = {
    target: Array.from(allFacts),
    answered: Array.from(uniqueFacts),
    skipped: [],
  };
  return newProgressData;
};
const handlePlay = (
  state: MatchState,
) => produce(state, (draft) => {
  const {
    games,
    gameStates,
  } = state;
  const nextGameIndex = gameStates.length;
  const nextGame = games[nextGameIndex];
  if (nextGame) {
    const gameState = toGameState(nextGame);
    draft.currentGameState = gameState;
    draft.reviewGameState = gameState;
  } else {
    draft.currentGameState = null;
  }
  draft.currentGameId = nextGame.id;
  draft.answeredEvents = 0;
});

const handleMatch = (
  leftIndex: number,
  rightIndex: number,
  startTime: number,
  endTime: number,
  state: MatchState,
) => produce(state, (draft) => {
  const {
    currentGameState,
    answeredFacts,
    uniqueAnswers,
  } = state;
  if (!currentGameState || leftIndex < 0 || rightIndex < 0) return;
  const {
    actions,
    game,
  } = currentGameState;

  const {
    sessionMeta,
  } = state;
  let {
    answeredEvents,
  } = state;
  const left = game[leftIndex];
  const right = game[rightIndex];

  const match = left.matches.find(l => l.matchId === right.matchId);
  const correct = !isEmpty(match);
  const time = new Date().toISOString();
  const timeTaken = (endTime - startTime) / 1000;
  const answers = [left, right];

  answeredEvents = correct ? answeredEvents + 1 : answeredEvents;
  const numUnansweredItems = currentGameState.game.filter(({ answered }) => !answered).length;

  const scores = calcScore(
    numUnansweredItems,
    answers,
    timeTaken,
    correct,
    sessionMeta,
    right.matchId,
  );

  const newSessionMeta = { ...sessionMeta };
  if (sessionMeta[right.matchId]) {
    newSessionMeta[right.matchId] = {
      lastTime: endTime,
      numSuccessAttempts: correct ? sessionMeta[right.matchId].numSuccessAttempts + 1 : 0,
      numTotalAttempts: (sessionMeta[right.matchId].numTotalAttempts + 1),
    };
  } else {
    newSessionMeta[right.matchId] = {
      lastTime: endTime,
      numSuccessAttempts: correct ? 1 : 0,
      numTotalAttempts: 1,
    };
  }

  draft.sessionMeta = newSessionMeta;
  draft.answeredEvents = answeredEvents;

  const updateUniqueAnsweredFacts = () => {
    scores.forEach(({
      factId,
      termId,
      contentId,
      score,
      previousScore,
    }) => {
      const currentAnsweredFact = {
        previousScore,
        currentScore: score,
        termId,
        studySetId: contentId,
      };
      if (factId) {
        answeredFacts[factId] = currentAnsweredFact;
        const previouslyHigh = previousScore
          ? previousScore >= THRESHOLD_MASTERY_NORMAL_SESSION : false;
        const currentlyHigh = score >= THRESHOLD_MASTERY_NORMAL_SESSION;
        const isItemAnswered = uniqueAnswers[factId];

        if (!isItemAnswered) {
          const newuniqueAnswerItem = {
            score,
            high: currentlyHigh,
            previouslyHigh,
            contentId,
          };
          uniqueAnswers[factId] = newuniqueAnswerItem;
        } else if (
          uniqueAnswers && isItemAnswered
        ) {
          uniqueAnswers[factId] = {
            score,
            high: currentlyHigh,
            previouslyHigh,
            contentId,
          };
        }
      }
    });
  };
  draft.answeredFacts = answeredFacts;
  updateUniqueAnsweredFacts();
  actions.push({
    left,
    right,
    correct,
    time,
    scores,
    timeTaken,
  });

  if (correct) {
    left.answered = true;
    right.answered = true;
  }
  draft.currentGameState = { ...currentGameState };
});
const endGame = (state: MatchState, timeTaken: number) => produce(state, (draft) => {
  const {
    currentGameState, games, reviewGameState,
  } = state;
  if (!currentGameState) return;
  // add only finished game
  const unansweredCards = currentGameState
    ? currentGameState.game.filter(({ answered }) => !answered) : [];

  if (unansweredCards.length === 0) {
    draft.gameStates.push({
      ...reviewGameState,
      actions: currentGameState.actions,
      endTime: new Date().toISOString(),
      timeTaken,
    });
  }
  draft.currentGameState = null;
  draft.reviewGameState = null;
  draft.currentGameId = null;
  draft.progressData = getProgressData(draft.gameStates, games);
});
const handleStop = (state: MatchState, timeTaken: number) => endGame(state, timeTaken);
const handleWin = (state: MatchState) => endGame(state, 0);

const endSession = (
  state: MatchState,
  data: *,
) => summarize(
  state,
  data,
);

const handleEndSession = (
  state: MatchState,
  data: *,
) => endSession(state,
  data);

const handleSessionComplete = (state: MatchState,
  data: *) => endSession(state,
  data);

const matchReducer = (
  state: MatchState = {
    ...initialState,
  },
  action: *,
) => {
  switch (action.type) {
    case START_SESSION:
      return handleStartSession(
        action.data.matchGame,
        state,
        action.data.id,
        action.data.learningOrder,
        action.data.distribution,
        action.data.hasMore,
      );
    case START_NEXT_BATCH:
      return handleNextBatch(
        action.data.matchGame,
        action.data.hasMore,
        state,
      );
    case PLAY:
      return handlePlay(
        state,
      );
    case MATCH:
      return handleMatch(
        action.data.leftIndex,
        action.data.rightIndex,
        action.data.startTime,
        action.data.endTime,
        state,
      );
    case STOP:
      return handleStop(state, action.data.timeTaken);
    case WIN:
      return handleWin(state);
    case STOP_SESSION:
      return handleEndSession(state,
        action.data);
    case COMPLETE_SESSION:
      return handleSessionComplete(state,
        action.data);
    case RESET_APP:
      return handleReset(state);
    default:
      return state;
  }
};

export default matchReducer;
